import React, { useCallback, useEffect, useState } from 'react';
import { Button, Table } from 'react-bootstrap';
import { FaExclamationTriangle, FaPencilAlt, FaPlus, FaTrash } from 'react-icons/fa';
import { toast } from 'react-toastify';
import {
  excluirVigenciaValorHoraPj,
  listarHistoricoValorHoraPj,
  obterCoberturaValorHoraPj,
} from '~/Services/Service';
import {
  TIPO_REMUNERACAO_FIXO,
  TIPO_REMUNERACAO_HORA,
  descreverTipoRemuneracao,
  formatarPercentuaisRetencao,
  formatarRemuneracao,
} from '../utils/formatters';
import ModalVigenciaValorHora from './ModalVigenciaValorHora';

const moeda = valor => Number(valor || 0).toLocaleString('pt-BR', {
  style: 'currency',
  currency: 'BRL',
});

// Badge azul (Hora) / verde (Fixo) — leitura instantânea da modalidade.
// Reusa as classes utilitárias hpj-badge existentes.
const classeBadgeModalidade = (tipo) => {
  if (tipo === TIPO_REMUNERACAO_HORA) return 'hpj-badge hpj-badge--blue';
  if (tipo === TIPO_REMUNERACAO_FIXO) return 'hpj-badge hpj-badge--green';
  return 'hpj-badge hpj-badge--slate';
};

const formatarCompetencia = data => {
  if (!data) return null;
  const partes = String(data).slice(0, 10).split('-');
  if (partes.length < 2) return null;
  return `${partes[1]}/${partes[0]}`;
};

const origemDescricao = origem => ({
  contrato: 'Contrato',
  edicao_fechamento: 'Edição Fechamento',
  reajuste_manual: 'Reajuste Manual',
}[origem] || origem || '-');

const TabHistoricoVH = ({ codigoPj }) => {
  const [itens, setItens] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [modalAberto, setModalAberto] = useState(false);
  const [vigenciaSelecionada, setVigenciaSelecionada] = useState(null);
  const [competenciasDescobertas, setCompetenciasDescobertas] = useState([]);

  const carregar = useCallback(() => {
    if (!codigoPj) return;
    setCarregando(true);
    Promise.all([
      listarHistoricoValorHoraPj(codigoPj),
      obterCoberturaValorHoraPj(codigoPj),
    ])
      .then(([historico, cobertura]) => {
        setItens(Array.isArray(historico) ? historico : []);
        setCompetenciasDescobertas(Array.isArray(cobertura) ? cobertura : []);
      })
      .catch(err => toast.error(err && err.message ? err.message : 'Erro ao carregar histórico.'))
      .finally(() => setCarregando(false));
  }, [codigoPj]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const handleAdicionar = () => {
    setVigenciaSelecionada(null);
    setModalAberto(true);
  };

  const handleEditar = item => {
    setVigenciaSelecionada(item);
    setModalAberto(true);
  };

  const handleExcluir = async item => {
    const descricao = item.data_fim_vigencia
      ? `de ${formatarCompetencia(item.data_inicio_vigente)} a ${formatarCompetencia(item.data_fim_vigencia)}`
      : `vigente a partir de ${formatarCompetencia(item.data_inicio_vigente)}`;
    const remuneracao = formatarRemuneracao(item.tipo_remuneracao, item.valor_hora, item.valor_fixo, item.horas_base);
    const ok = window.confirm(`Excluir vigência (${descricao}) — ${remuneracao}?`);
    if (!ok) return;

    try {
      await excluirVigenciaValorHoraPj(item.codigo);
      toast.success('Vigência excluída.');
      carregar();
    } catch (err) {
      toast.error(err && err.message ? err.message : 'Erro ao excluir vigência.');
    }
  };

  const quantidadeDescobertas = competenciasDescobertas.length;
  const competenciasFormatadas = competenciasDescobertas
    .slice(0, 6)
    .map(formatarCompetencia)
    .filter(Boolean)
    .join(', ');
  const sufixoDescobertas = quantidadeDescobertas > 6 ? ` e mais ${quantidadeDescobertas - 6}` : '';

  return (
    <>
      {quantidadeDescobertas > 0 && (
        <div className="hpj-vh-aviso">
          <FaExclamationTriangle className="hpj-vh-aviso-icon" />
          <div>
            <strong>
              {quantidadeDescobertas} fechamento(s) em competência(s) sem vigência de valor-hora cadastrada.
            </strong>
            <div className="hpj-vh-aviso-detalhe">
              {competenciasFormatadas}{sufixoDescobertas}
            </div>
          </div>
        </div>
      )}

      <div className="hpj-toolbar">
        <Button
          className="hpj-btn hpj-btn-primary"
          variant="primary"
          onClick={handleAdicionar}
        >
          <FaPlus className="mr-2" /> Adicionar vigência
        </Button>
      </div>

      <div className="hpj-card hpj-card--overflow d-none d-md-block">
        <Table className="hpj-table" responsive>
          <thead>
            <tr>
              <th>Modalidade</th>
              <th>Remuneração</th>
              <th>Retenções</th>
              <th>Competência início</th>
              <th>Competência fim</th>
              <th>Origem</th>
              <th style={{ width: 120 }}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {itens.map(item => (
              <tr key={item.codigo}>
                <td>
                  <span className={classeBadgeModalidade(item.tipo_remuneracao)}>
                    {descreverTipoRemuneracao(item.tipo_remuneracao)}
                  </span>
                </td>
                <td className="hpj-main-text">
                  {formatarRemuneracao(item.tipo_remuneracao, item.valor_hora, item.valor_fixo, item.horas_base)}
                </td>
                <td className="hpj-muted">
                  {formatarPercentuaisRetencao(item.percentual_13, item.percentual_14, item.percentual_fundo)}
                </td>
                <td>{formatarCompetencia(item.data_inicio_vigente) || '-'}</td>
                <td>{formatarCompetencia(item.data_fim_vigencia) || 'Vigente'}</td>
                <td>
                  <span className="hpj-badge hpj-badge--slate">
                    {origemDescricao(item.origem)}
                  </span>
                </td>
                <td>
                  <Button
                    className="hpj-icon-btn"
                    title="Editar vigência"
                    onClick={() => handleEditar(item)}
                  >
                    <FaPencilAlt />
                  </Button>
                  <Button
                    className="hpj-icon-btn hpj-icon-btn-danger"
                    title="Excluir vigência"
                    onClick={() => handleExcluir(item)}
                  >
                    <FaTrash />
                  </Button>
                </td>
              </tr>
            ))}
            {!itens.length && (
              <tr>
                <td colSpan="7" className="text-center text-muted py-4">
                  {carregando ? 'Carregando...' : 'Nenhuma vigência cadastrada.'}
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
      <div className="d-md-none hpj-tab-mobile-list">
        {itens.map(item => (
          <div className="hpj-mobile-card" key={item.codigo}>
            <div className="hpj-mobile-card-head">
              <div className="hpj-mobile-title-block">
                <div className="hpj-mobile-title">
                  {formatarRemuneracao(item.tipo_remuneracao, item.valor_hora, item.valor_fixo, item.horas_base)}
                </div>
                <div className="hpj-mobile-subtitle">
                  {formatarCompetencia(item.data_inicio_vigente) || '-'} até {formatarCompetencia(item.data_fim_vigencia) || 'Vigente'}
                </div>
              </div>
              <div className="hpj-mobile-badges">
                <span className={classeBadgeModalidade(item.tipo_remuneracao)}>
                  {descreverTipoRemuneracao(item.tipo_remuneracao)}
                </span>
                <span className="hpj-badge hpj-badge--slate">
                  {origemDescricao(item.origem)}
                </span>
              </div>
            </div>
            <div className="hpj-mobile-grid">
              <div className="hpj-mobile-field">
                <span>Competência início</span>
                <strong>{formatarCompetencia(item.data_inicio_vigente) || '-'}</strong>
              </div>
              <div className="hpj-mobile-field">
                <span>Competência fim</span>
                <strong>{formatarCompetencia(item.data_fim_vigencia) || 'Vigente'}</strong>
              </div>
              <div className="hpj-mobile-field">
                <span>Retenções</span>
                <strong>{formatarPercentuaisRetencao(item.percentual_13, item.percentual_14, item.percentual_fundo)}</strong>
              </div>
            </div>
            <div className="hpj-mobile-actions">
              <Button
                className="hpj-mobile-action-btn"
                size="sm"
                variant="outline-primary"
                onClick={() => handleEditar(item)}
              >
                <FaPencilAlt className="mr-1" /> Editar
              </Button>
              <Button
                className="hpj-mobile-action-btn hpj-mobile-action-btn--danger"
                size="sm"
                variant="outline-danger"
                onClick={() => handleExcluir(item)}
              >
                <FaTrash className="mr-1" /> Excluir
              </Button>
            </div>
          </div>
        ))}
        {!itens.length && (
          <div className="hpj-mobile-empty">
            {carregando ? 'Carregando...' : 'Nenhuma vigência cadastrada.'}
          </div>
        )}
      </div>

      <ModalVigenciaValorHora
        show={modalAberto}
        onHide={() => setModalAberto(false)}
        codigoPj={codigoPj}
        vigencia={vigenciaSelecionada}
        onSalvo={carregar}
      />
    </>
  );
};

export default TabHistoricoVH;
